import {filter} from 'lodash';
import {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {userApi} from 'api';
import {PredefinedNotifierType} from 'components';
import {Copyright} from 'components/Copyright';
import {UserContext} from 'context';
import {useApiData, useObservable} from 'hooks';
import {OnlineSessionsStore, SessionStatus} from 'interfaces';
import {onlineSessionsStore, authDataStore} from 'services';

import {MainHeader} from './MainHeader';
import {MainMenu} from './MainMenu';
import {getMenuItems} from './get-menu-items';

type Props = {
    children: React.ReactNode;
    dynamicHeader?: React.ReactNode;
};

export const AuthLayoutControls: React.FC<Props> = ({children, dynamicHeader}: Props) => {
    const {t} = useTranslation();
    const {settings} = useContext(UserContext);

    const {localUser: {isAvailable, notifications}} = useObservable(authDataStore);

    const [incomingSessionCount, setIncomingSessionCount] = useState(0);

    const {send: setAvailable} = useApiData(userApi.setAvailable, {notificationType: PredefinedNotifierType.Save});
    const {send: setNotificationEnabled} = useApiData(userApi.setNotificationsEnabled, {notificationType: PredefinedNotifierType.Save});

    const clientConnectionsEnabled = useMemo(() => settings.featuresAvailability.clientsConnections, [settings]);

    const menuItems = useMemo(() => getMenuItems({
        t, clientConnectionsEnabled, incomingSessionCount
    }), [t, clientConnectionsEnabled, incomingSessionCount]);

    const onAvailabilityChange = useCallback((value: boolean) => {
        setAvailable(value);
    }, [setAvailable]);

    const onNotificationsEnabledChange = useCallback((value: boolean) => {
        setNotificationEnabled(value);
    }, [setNotificationEnabled]);

    const onSessionsUpdated = useCallback((data: OnlineSessionsStore) => {
        const incomingSessions = filter(data.sessions,
            (session) => session.status === SessionStatus.Calling || session.status === SessionStatus.New
        );
        setIncomingSessionCount(incomingSessions.length);
    }, []);

    useEffect(() => onlineSessionsStore.subscribe(onSessionsUpdated), [onSessionsUpdated]);

    return (
        <div className='tw-bg-layout tw-overflow-hidden tw-min-h-full tw-flex tw-flex-col'>
            <MainHeader
                menuItems={menuItems}
                isAvailable={isAvailable}
                dynamicHeader={dynamicHeader}
                notificationsEnabled={notifications?.enabled}
                onAvailabilityChange={onAvailabilityChange}
                onNotificationsEnabledChange={onNotificationsEnabledChange}
            />
            <MainMenu menuItems={menuItems} />
            <main className='tw-flex tw-flex-grow tw-overflow-hidden lg:tw-pl-52 tw-py-20 lg:tw-pb-0'>
                <div className='tw-px-6 lg:tw-px-12 tw-pb-4 tw-overflow-x-auto tw-flex-grow'>
                    {children}
                </div>
            </main>
            <footer className='tw-h-20 tw-hidden lg:tw-flex tw-items-center tw-text-default-dimmed tw-px-6'>
                <Copyright />
            </footer>
        </div>
    );
};
